import { format } from 'date-fns'

import { DDELicensePayload, DDELicensePeriodPayload } from '@img-arena/consoles-api-dde'
import { DATE_FORMAT } from '@/constants/dateFormat'
import { UseCompetitionsType } from '@/constants/licenses'
import { ddeApi } from '@/service/ddeApi'
import { LabelValue } from '@/types/common'
import { LicenseProperty } from '@/types/dde/license'
import { LicenseProperties } from '@/utils/api/licenseProperties'
import { isMsfpLicense } from '@/utils/licenseUtils'
import { UpdateDDELicense } from '@/utils/licenseUtils.types'

/**
 * Creates a save or update DDE license period payload for a license property
 * @param period License Period object excluding the subscription.competitionIds
 * @param selectedCompetitionsOrSeasons label value pair array of competitions or seasons selected for the license period
 * @param useAllCompetitions either COMPETITIONS_ALL or COMPETITIONS_PARTIAL to allow wildcard competition access for a license property
 * @param licenseProperty license property
 * @param competitionIds Array of strings of competition ids (not season ids)
 */

type CreateDDELicensePeriodPayloadArgs = {
  period: DDELicensePeriodPayload
  selectedCompetitionsOrSeasons: LabelValue[]
  useAllCompetitions: UseCompetitionsType
  licenseProperty: string | number
  competitionIds?: string[]
}

export const createDDELicensePeriodPayload = ({
  period,
  selectedCompetitionsOrSeasons,
  useAllCompetitions,
  licenseProperty,
  competitionIds
}: CreateDDELicensePeriodPayloadArgs): DDELicensePeriodPayload => {
  const { ...licensePeriod } = period

  const licensePeriodPayload: DDELicensePeriodPayload = {
    ...licensePeriod,
    startDate: format(period.startDate, DATE_FORMAT.DATE),
    endDate: format(period.endDate, DATE_FORMAT.DATE)
  }

  if (isMsfpLicense(licenseProperty)) {
    licensePeriodPayload.subscription = {
      competitionIds: competitionIds || selectedCompetitionsOrSeasons.map((comp) => comp.value),
      type: useAllCompetitions
    }
  }

  return licensePeriodPayload
}

/**
 * Updates a DDE license
 * Periods that are preexisting need updating in a PUT request
 * New periods are added via a POST request
 */
export const updateDDELicense = async ({
  operatorId,
  licenseProperty,
  licensePeriods,
  selectedCompetitionsOrSeasons,
  competitionIds,
  useAllCompetitions
}: UpdateDDELicense): Promise<PromiseSettledResult<{ message: string }>[]> => {
  const promises: Promise<{ message: string }>[] = []

  const newLicensePeriods = licensePeriods?.filter((licensePeriod) => !licensePeriod.id)
  if (newLicensePeriods && newLicensePeriods.length > 0) {
    const createData = {
      property: licenseProperty.value.toString().toUpperCase(),
      licensingPeriods: newLicensePeriods.map((licensePeriod) =>
        createDDELicensePeriodPayload({
          period: licensePeriod,
          selectedCompetitionsOrSeasons,
          useAllCompetitions,
          licenseProperty: licenseProperty.value,
          competitionIds
        })
      )
    }

    promises.push(
      postDDEOperatorLicense(Number(operatorId), createData) as Promise<{ message: string }>
    )
  }

  const existingLicensePeriods = licensePeriods?.filter((licensePeriod) => licensePeriod.id)
  if (existingLicensePeriods && existingLicensePeriods.length > 0) {
    const updatePromises = existingLicensePeriods.map((licensePeriod) => {
      const data = {
        id: licensePeriod.id,
        startDate: licensePeriod.startDate,
        endDate: licensePeriod.endDate,
        numOfBookedEvents: licensePeriod.numOfBookedEvents || 0,
        numOfConsumedEvents: licensePeriod.numOfConsumedEvents || 0,
        unlimited: licensePeriod.unlimited || false,
        suspended: licensePeriod.suspended || false,
        numOfEvents: licensePeriod.numOfEvents || 0,
        subscription: { type: useAllCompetitions, competitionIds: competitionIds || [] }
      }

      return putOperatorsLicensesProperty({
        operatorId,
        licensePeriodId: licensePeriod.id!.toString(),
        licenseProperty: licenseProperty.value.toString().toUpperCase(),
        data
      })
    })

    promises.push(...updatePromises)
  }

  return Promise.allSettled(promises)
}

type SaveDDELicenseArgs = {
  operatorId: number
  competitions: LabelValue[]
  licensePeriods: DDELicensePeriodPayload[]
  licenseProperty: LicenseProperty['property']
  useAllCompetitions: UseCompetitionsType
}

export const saveDDELicense = async ({
  operatorId,
  competitions,
  licensePeriods,
  licenseProperty,
  useAllCompetitions
}: SaveDDELicenseArgs): Promise<{ message: string }> => {
  const payload = {
    property: licenseProperty,
    licensingPeriods: licensePeriods.map((period) =>
      createDDELicensePeriodPayload({
        period,
        selectedCompetitionsOrSeasons: competitions,
        useAllCompetitions,
        licenseProperty
      })
    )
  }

  return postDDEOperatorLicense(operatorId, payload)
}

export const formatDDELicenseProperties = (properties: string[]) => {
  const hiddenLicenseProperties = import.meta.env.VITE_HIDDEN_LICENSE_PROPERTIES || ''
  const splitHiddenLicenseProperties = hiddenLicenseProperties.split(',').filter(Boolean)
  const filteredProperties = properties.filter((property) => {
    if (splitHiddenLicenseProperties.length) {
      return !splitHiddenLicenseProperties.includes(property)
    }
    return true
  })
  return filteredProperties.reduce((acc: LicenseProperties, curr) => {
    const property = {
      label: curr,
      value: curr
    }
    return [...acc, property]
  }, [])
}

type PutOperatorsLicensesPropertyArgs = {
  operatorId: string
  licensePeriodId: string
  licenseProperty: string
  data: DDELicensePeriodPayload
}

export async function putOperatorsLicensesProperty({
  operatorId,
  licensePeriodId,
  licenseProperty,
  data
}: PutOperatorsLicensesPropertyArgs): Promise<{ message: string }> {
  const res = await ddeApi.endpoints.operators.updateLicense({
    operatorId: Number(operatorId),
    licenseProperty,
    periodId: Number(licensePeriodId),
    data
  })

  return res.data as { message: string }
}

export async function postDDEOperatorLicense(
  operatorId: number,
  data: DDELicensePayload
): Promise<{ message: string }> {
  const res = await ddeApi.endpoints.operators.addLicense(operatorId, data)

  return res.data as { message: string }
}
